import { FclFtlTransport } from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/TransportTypesParameters/types';
import { GraphQLQuery } from '@settings/services/graphQLClient/GraphQLClient';
import gql from 'graphql-tag';

import { FilterData, RouteCalculationParams, SortData } from './interfaces';

// Запрос расчета маршрутов грузоперевозки по параметрам пользователя. Выполняет расчет/сортировку/пагинацию для расчета.
export class RouteSearchQuery implements GraphQLQuery<null> {
  readonly query: any;

  readonly variables: null;

  constructor(
    params: RouteCalculationParams,
    isArchive: boolean,
    filterParameters: Partial<FilterData> | undefined,
    sort: SortData,
    taskId: string,
    isSearchByTransportParams: boolean
  ) {
    this.variables = null;
    const containerParameters: string[] =
      params.cargoType === 'container'
        ? params.containerParameters?.containerParameters.map((container) => {
            const pallets = container.palletsLoaded.map(
              (pallet) => `{
				type: ${pallet.type},
				quantity: ${pallet.quantity},
				weight: ${pallet.weight}
			  }`
            );
            return `{
				containerId: ${container.containerId},
				containerQuantity: ${container.containerQuantity},
				eachContainerWeight: ${container.eachContainerWeight},
				isNeedContainerRent: ${params.isNeedContainerRent},
				withPallets: ${container.withPallets ? 'true' : 'false'},
				palletsLoaded: [${pallets.join(',')}]
			}`;
          }) || []
        : [];

    const truckParameters: string[] =
      params.cargoType === 'auto'
        ? params.truckParameters?.truckParameters.map((truck) => {
            const pallets = truck.palletsLoaded.map(
              (pallet) => `{
				type: ${pallet.type},
				quantity: ${pallet.quantity},
				weight: ${pallet.weight}
			  }`
            );
            return `{
				truckId: ${truck.truckId},
				truckQuantity: ${truck.truckQuantity},
				eachTruckWeight: ${truck.eachTruckWeight},
				withPallets: ${truck.withPallets ? 'true' : 'false'},
				palletsLoaded: [${pallets.join(',')}]
     		}`;
          }) || []
        : [];

    const customCargoParameters: string[] = ['custom', 'air', 'fclFtl'].includes(params.cargoType)
      ? params.customCargoParameters?.customCargoParameters.map((cargo) => {
          const type =
            cargo.parametersCargoCalcType === 'base'
              ? cargo.parametersType
              : cargo.parametersCargoCalcType;
          return `{
              customCargoType: ${type},
              baseParameters: {
                quantity: ${cargo.baseParameters.quantity},
                length: ${cargo.baseParameters.length},
                width: ${cargo.baseParameters.width},
                height: ${cargo.baseParameters.height},
                weight: ${cargo.baseParameters.weight}
              },
              palletParameters:{
                type: ${cargo.palletParameters.type}
                quantity: ${cargo.palletParameters.quantity}
                normativeHeightOfStacking: ${cargo.palletParameters.normativeHeightOfStacking}
                weight: ${cargo.palletParameters.weight}
              },
              volumeParameters:{
                volume: ${cargo.volumeParameters.volume}
                weight: ${cargo.volumeParameters.weight}
              }
            }`;
        }) || []
      : [];

    const toLocationType =
      params.endLocation?.type === 'terminal' && params.endLocation?.symbolCode === 'ANY'
        ? 'location'
        : params.endLocation?.type;
    const fromLocationType =
      params.startLocation?.type === 'terminal' && params.startLocation?.symbolCode === 'ANY'
        ? 'location'
        : params.startLocation?.type;

    let isDangerousCargo =
      params.cargoType === 'container'
        ? params.containerParameters?.isDangerousCargo
        : params.customCargoParameters?.isDangerousCargo;

    if (params.cargoType === 'auto') {
      isDangerousCargo = params.truckParameters?.isDangerousCargo;
    }

    let targetCurrencyId =
      params.cargoType === 'container'
        ? params.containerParameters?.targetCurrencyId
        : params.customCargoParameters?.targetCurrencyId;

    if (params.cargoType === 'auto') {
      targetCurrencyId = params.truckParameters?.targetCurrencyId;
    }

    let cargoType = params.cargoType === 'container' ? 'container' : 'custom';

    if (params.cargoType === 'auto') {
      cargoType = 'truck';
    }

    const cargoFingerprint =
      params.customCargoParameters.cargoFingerprint.length > 0
        ? `cargoFingerprint: "${params.customCargoParameters.cargoFingerprint}",`
        : null;

    const containerTransportTypes: FclFtlTransport[] = [];
    const truckTransportTypes: FclFtlTransport[] = [];

    params.customCargoParameters.transportTypes.forEach((transportType) => {
      if (transportType.type === 'container') {
        containerTransportTypes.push(transportType);
        return;
      }
      truckTransportTypes.push(transportType);
    });

    const containerTypes =
      containerTransportTypes.length > 0
        ? `containerTypes: [${containerTransportTypes
            .map(
              (container) => `{
					transportTypeId: ${container.transportTypeId},
					eachWeight: ${container.weight},
					quantity: 1
			}`
            )
            .join(',')}],`
        : null;

    const truckTypes =
      truckTransportTypes.length > 0
        ? `truckTypes: [${truckTransportTypes
            .map(
              (truck) => `{
					transportTypeId: ${truck.transportTypeId},
					eachWeight: ${truck.weight},
					quantity: 1
			}`
            )
            .join(',')}],`
        : null;

    const searchByTransportParams = isSearchByTransportParams
      ? `searchByTransportParams: true,`
      : null;

    const mixedTransportParams =
      params.cargoType === 'fclFtl'
        ? `mixedTransport: {
				enabled: true,
				${cargoFingerprint ?? ''}
				${containerTypes ?? ''}
				${truckTypes ?? ''}
				${searchByTransportParams ?? ''}
			}`
        : null;

    const parametersQuery = `{
      	endTransportingConditionId: ${params.endTransportingCondition},
	  	isArchiveCalculation: ${isArchive ? 'true' : 'false'},
		isNeedPreCarriage: ${params.isNeedPrekeridge ? 'true' : 'false'},
		isDangerousCargo: ${isDangerousCargo},
		requiredTransportingTypes: [${params.requiredTransportingTypes.join(',')}], 
		date: "${params.startExpeditionDate?.toISOString()}",
		customCargoParameters: [
			${customCargoParameters.join(',')}
		], 
		startTransportingConditionId: ${params.startTransportingCondition},
		cargoType: ${cargoType}, 
		containerParameters: [
			${containerParameters.join(',')}
		],
		truckParameters: [
			${truckParameters.join(',')}
		],
		routePoints: {
			from: {${fromLocationType}: ${params.startLocation?.id}},
			to: {${toLocationType}: ${params.endLocation?.id}}
		},
		targetCurrencyId: ${targetCurrencyId},
		${mixedTransportParams ?? ''}
    }`;

    let filterData: string | undefined;
    if (filterParameters) {
      let containerRentTypes = ['COC', 'SOC_RENT'];
      if (params.isNeedContainerRent) {
        containerRentTypes = filterParameters.containerRentType
          ? filterParameters.containerRentType
          : containerRentTypes;
      }

      filterData = `{
        ${filterParameters.carriers ? `carriers: [${filterParameters.carriers.join(',')}],` : ''}
        ${
          filterParameters.contractors
            ? `contractors: [${filterParameters.contractors.join(',')}],`
            : ``
        }
        ${
          filterParameters.deliveryTime &&
          (filterParameters.deliveryTime.min > 0 || filterParameters.deliveryTime.max > 0)
            ? `deliveryTime: {
          ${
            filterParameters.deliveryTime.max > 0 ? `max: ${filterParameters.deliveryTime.max}` : ``
          }
          ${
            filterParameters.deliveryTime.min > 0 ? `min: ${filterParameters.deliveryTime.min}` : ``
          }
        },`
            : `deliveryTime: { min: -1, max: -1 }`
        }
        ${
          filterParameters.price &&
          (filterParameters.price.min > 0 || filterParameters.price.max > 0)
            ? `price: {
          ${filterParameters.price.max > 0 ? `max: ${filterParameters.price.max}` : ``}
          ${filterParameters.price.min > 0 ? `min: ${filterParameters.price.min}` : ``}
        },`
            : `price: { min: -1, max: -1 }`
        }
        ${
          filterParameters.transportingTypes
            ? `transportingTypes: [${filterParameters.transportingTypes.join(',')}],`
            : ``
        }
        ${filterParameters.terminals ? `terminals: [${filterParameters.terminals.join(',')}],` : ``}
        ${
          filterParameters.startTerminals
            ? `startTerminals: [${filterParameters.startTerminals.join(',')}],`
            : ``
        }
        ${
          filterParameters.endTerminals
            ? `endTerminals: [${filterParameters.endTerminals.join(',')}],`
            : ``
        }
        ${containerRentTypes ? `containerRentType: [${containerRentTypes.join(',')}],` : ``}
        isSingleShoulderRoutesOnly: ${
          filterParameters.isSingleShoulderRoutesOnly ? 'true' : 'false'
        },
		isNeedPreCarriageRoutes: ${filterParameters.isNeedPrekeridgeRoutes ? 'true' : 'false'},
		isNeedRegularRoutes: ${filterParameters.isNeedRegularRoutes ? 'true' : 'false'},
		exactSearch: ${
      filterParameters.alternativeRoutes?.exactSearch || filterParameters.alternativeRoutes === null
        ? 'true'
        : 'false'
    },
		fromTerminalRadius: ${filterParameters.alternativeRoutes?.fromTerminalRadius ? 'true' : 'false'},
		toTerminalRadius: ${filterParameters.alternativeRoutes?.toTerminalRadius ? 'true' : 'false'},
		withoutOnCarriage: ${filterParameters.alternativeRoutes?.withoutOnCarriage ? 'true' : 'false'},
		withoutPreCarriage: ${filterParameters.alternativeRoutes?.withoutPreCarriage ? 'true' : 'false'},
		withPreCarriage: ${filterParameters.alternativeRoutes?.withPreCarriage ? 'true' : 'false'},
		withCrossDocking: ${filterParameters.alternativeRoutes?.withCrossDocking ? 'true' : 'false'}
      }`;
    }

    this.query = gql`
      query __SEARCH_ROUTES_QUERY__ {
        data: searchRoutes(
          cargoParameters: ${parametersQuery}
		  ${filterData ? `filter: ${filterData}` : ''}
          sorting: {
            by: "${sort.by}"
            direction: "${sort.direction}"
          }
		  pagination: {
		  	limit: ${params.limit}
			offset: ${params.offset}
		  }
		  taskId: ${taskId}
        ) {
          total
		  countExactSearch
		  countFromTerminalRadius
		  countToTerminalRadius
		  countWithPreCarriage
		  countWithoutOnCarriage
		  countWithoutPreCarriage
		  countWithCrossDocking
          isNotCompleteResult
            routes {
              routes {
			  	mixedTransport {
					cargoFingerprint
					stepKinds
					transportSelectionKey
					containerLoads {
						usedWeight
						usedVolume
						transportTypeId
						usedPallets {
							count
							palletType
						}
						items {
							cargoItemId
							isPallet
							palletType
							volume
							weight
						}
					}
					truckLoads {
						usedWeight
						usedVolume
						transportTypeId
						usedPallets {
							count
							palletType
						}
						items {
							cargoItemId
							isPallet
							palletType
							volume
							weight
						}
					}
					crossDockingOperations {
						allowances {
							allowanceOffer {
								allowance {
									allowance_group
								}
								allowance_id
								is_invoice_allowance
								id
								offer_conditions
							}
							allowanceOfferCondition {
								currency_id
								currency {
								code
								}
								group_num
								id
								information_price
								is_fixed_price
								is_max_value_not_limited
								is_min_value_not_limited
								is_tax_included_in_price
								max_value
								min_value
								minimal_payment_price
								price
								tax_id
								unit_id
							}
							calculatedPrice {
								basePrice
								computedAmount
								conversionFee
								fullPriceInTargetCurrency
								priceInTargetCurrency
								taxInTargetCurrency
							}
                   		}
						afterStepIndex
						calculatedPrice {
							basePrice
							computedAmount
							conversionFee
							fullPriceInTargetCurrency
							priceInTargetCurrency
							taxInTargetCurrency
                    	}
						crossDockingOfferId
						fromKind
						offerConditionIds
						offerConditions {
							tax_id
							currency_id
						}
						terminalId
						terminalOfferId
						toKind
						priceParts {
							calculatedPrice {
								basePrice
								computedAmount
								conversionFee
								fullPriceInTargetCurrency
								priceInTargetCurrency
								taxInTargetCurrency
                    		}
							kind
							offerCondition {
								tax_id
								currency_id
								currency {
                          			code
                        		}
							}
							offerConditionId
							unitGroup
							unit_group {
								id
								default_name
								unit_symbol
								cargo_type_groups
								rounding_rule
								accuracy
							}
						}
					}
				}
                steps {
                    endTerminal {
                      default_abbreviation
                      default_name
                      id
                      localized_abbreviations
                      localized_names
                      location_id
                      files
                      symbolCode: symbol_code
                    }
                    endTerminalAllowances {
                      allowanceOffer {
                        allowance {
                          allowance_group
                        }
                        is_invoice_allowance
                        allowance_id
                        id
                        offer_conditions
                      }
                      allowanceOfferCondition {
                        currency_id
                        currency {
                          code
                        }
                        group_num
                        id
                        information_price
                        is_fixed_price
                        is_max_value_not_limited
                        is_min_value_not_limited
                        is_tax_included_in_price
                        max_value
                        min_value
                        minimal_payment_price
                        price
                        tax_id
                        unit_id
                      }
                      calculatedPrice {
                        basePrice
                        computedAmount
                        conversionFee
                        fullPriceInTargetCurrency
                        priceInTargetCurrency
                        taxInTargetCurrency
                      }
                    }
                    endTerminalCalculatedPrice {
                      basePrice
                      computedAmount
                      conversionFee
                      fullPriceInTargetCurrency
                      priceInTargetCurrency
                      taxInTargetCurrency
                    }
                    endTerminalLoadingUnloadingOffer {
                      allowance_offers
                      id
                      is_loading_to_unknown_transport
                      is_unloading_from_unknown_transport
                      loading_shoulder_types
                      offer_conditions
                      service_type
                      unloading_shoulder_types
                    }
                    endTerminalOffer {
                      cargo_type_group
                      containers
                      delivery_modes
                      id
                      loading_offers
                      terminal_id
					  trucks
                    }
                    endTerminalPrice {
                      currency_id
                      group_num
                      id
                      information_price
                      is_fixed_price
                      is_max_value_not_limited
                      is_min_value_not_limited
                      is_tax_included_in_price
                      max_value
                      min_value
                      minimal_payment_price
                      price
                      tax_id
                      unit_id
                    }
                    startTerminal {
                      default_abbreviation
                      default_name
                      id
                      localized_abbreviations
                      localized_names
                      location_id
                      files
                      symbolCode: symbol_code
                    }
                    startTerminalAllowances {
                      allowanceOffer {
                        allowance {
                         allowance_group
                        }
                        is_invoice_allowance
                        allowance_id
                        id
                        offer_conditions
                      }
                      allowanceOfferCondition {
                        currency_id
                         currency {
                          code
                        }
                        group_num
                        id
                        information_price
                        is_fixed_price
                        is_max_value_not_limited
                        is_min_value_not_limited
                        is_tax_included_in_price
                        max_value
                        min_value
                        minimal_payment_price
                        price
                        tax_id
                        unit_id
                      }
                      calculatedPrice {
                        basePrice
                        computedAmount
                        conversionFee
                        fullPriceInTargetCurrency
                        priceInTargetCurrency
                        taxInTargetCurrency
                      }
                    }
                    startTerminalCalculatedPrice {
                      basePrice
                      computedAmount
                      conversionFee
                      fullPriceInTargetCurrency
                      priceInTargetCurrency
                      taxInTargetCurrency
                    }
                    startTerminalLoadingUnloadingOffer {
                      allowance_offers
                      id
                      is_loading_to_unknown_transport
                      is_unloading_from_unknown_transport
                      loading_shoulder_types
                      offer_conditions
                      service_type
                      unloading_shoulder_types
                    }
                    startTerminalOffer {
                      cargo_type_group
                      containers
                      delivery_modes
                      id
                      loading_offers
                      terminal_id
					  trucks
                    }
                    startTerminalPrice {
                      currency_id
                       currency {
                        code
                      }
                      group_num
                      id
                      information_price
                      is_fixed_price
                      is_max_value_not_limited
                      is_min_value_not_limited
                      is_tax_included_in_price
                      max_value
                      min_value
                      minimal_payment_price
                      price
                      tax_id
                      unit_id
                    }
                    shoulder {
                      carrier_id
                      contractor_id
                      distance
                      distance_unit
                      from_location_ids
                      from_terminal_ids
                      id
                      shoulder_type
                      to_location_ids
                      to_terminal_ids
                      shoulderSteps {
                        id
                        position
                        transportType {
                          id
                          default_name
                          localized_names
                          transporting_default_name
                          transporting_names
                        }
                        startTerminal {
                          default_abbreviation
                          default_name
                          id
                          localized_abbreviations
                          localized_names
                          location_id
                          files
                          symbolCode: symbol_code
                        }
                        endTerminal {
                          default_abbreviation
                          default_name
                          id
                          localized_abbreviations
                          localized_names
                          location_id
                          files
                          symbolCode: symbol_code
                        }
                      }
                    }
                    shoulderAllowances {
                      allowanceOffer {
                        allowance {
                         allowance_group
                        }
                        allowance_id
                        is_invoice_allowance
                        id
                        offer_conditions
                      }
                      allowanceOfferCondition {
                        currency_id
                        currency {
                          code
                        }
                        group_num
                        id
                        information_price
                        is_fixed_price
                        is_max_value_not_limited
                        is_min_value_not_limited
                        is_tax_included_in_price
                        max_value
                        min_value
                        minimal_payment_price
                        price
                        tax_id
                        unit_id
                      }
                      calculatedPrice {
                        basePrice
                        computedAmount
                        conversionFee
                        fullPriceInTargetCurrency
                        priceInTargetCurrency
                        taxInTargetCurrency
                      }
                    }
					shoulderOfferAssignments {
					  weight
					  transportTypeId
					  transportKind
					  quantity
					  offerCondition {
						currency {
							course
							code
						}
						currency_id
						group_num
						id
						information_price
						is_fixed_price
						is_max_value_not_limited
						is_min_value_not_limited
						is_tax_included_in_price
						max_value
						min_value
						minimal_payment_price
						price
						tax_id
						unit_id
                      }
					  offer {
						active_from
						active_to 
						allowance_offers
						cargo_type_group
						container_affiliation_id
						container_nominal_weight
						containers
						trucks
						delivery_modes
						delivery_time
						free_time_for_container_usage_on_start_terminal
						free_time_for_container_usage_on_end_terminal
						id
						is_danger_cargo_allowed
						is_empty_container_collecting_included
						is_empty_container_returning_included
						loading_condition_id
						offer_conditions
						shoulder_id
						unloading_condition_id
                      }
					  calculatedPrice {
						basePrice
						computedAmount
						conversionFee
						fullPriceInTargetCurrency
						priceInTargetCurrency
						taxInTargetCurrency
                      }
					}
                    shoulderOffer {
                      active_from
                      active_to 
                      allowance_offers
                      cargo_type_group
                      container_affiliation_id
                      container_nominal_weight
                      containers
					  trucks
                      delivery_modes
                      delivery_time
                      free_time_for_container_usage_on_start_terminal
                      free_time_for_container_usage_on_end_terminal
                      id
                      is_danger_cargo_allowed
                      is_empty_container_collecting_included
                      is_empty_container_returning_included
                      loading_condition_id
                      offer_conditions
                      shoulder_id
                      unloading_condition_id
                    }
                    shoulderOfferCalculatedPrice {
                      basePrice
                      computedAmount
                      conversionFee
                      fullPriceInTargetCurrency
                      priceInTargetCurrency
                      taxInTargetCurrency
                    }
                    shoulderOfferPriceCondition {
                      currency {
                        course
                        code
                      }
                      currency_id
                      group_num
                      id
                      information_price
                      is_fixed_price
                      is_max_value_not_limited
                      is_min_value_not_limited
                      is_tax_included_in_price
                      max_value
                      min_value
                      minimal_payment_price
                      price
                      tax_id
                      unit_id
                    }
                    stepNumber
                  }
				  routeId
				  cargoID
                  fullPrice
                  dropOff {
                    carrier_id
                    container_ids
                    contractor_id
                    currency_id
                    currency {
                      code
                    }
                    from_location_ids
                    id
                    is_container_usage_in_another_contractor_service_allowed
                    is_tax_included
                    price
                    tax_id
                    to_location_ids
                  }
                  dropOffCalculatedPrice {
                    basePrice
                    computedAmount
                    conversionFee
                    fullPriceInTargetCurrency
                    priceInTargetCurrency
                    taxInTargetCurrency
                  }
                  pickOn {
                    carrier_id
                    container_ids
                    contractor_id
                    currency_id
                     currency {
                      code
                    }
                    from_location_ids
                    id
                    is_container_usage_in_another_contractor_service_allowed
                    is_tax_included
                    price
                    tax_id
                    to_location_ids
                  }
                  pickOnCalculatedPrice {
                    basePrice
                    computedAmount
                    conversionFee
                    fullPriceInTargetCurrency
                    priceInTargetCurrency
                    taxInTargetCurrency
                  }
                  containerRent {
                    carrier_id
                    container_ids
                    contractor_id
                    currency_id
                    from_location_ids
                    id
                    is_container_usage_in_another_contractor_service_allowed
                    is_tax_included
                    price
                    tax_id
                    to_location_ids
                  }
                  containerRentCalculatedPrice {
                    basePrice
                    computedAmount
                    conversionFee
                    fullPriceInTargetCurrency
                    priceInTargetCurrency
                    taxInTargetCurrency
                  }
               }
            deliveryTime
            routeToken
            fullPrice
			hasCrossDocking
			isAlternative
			searchType
			notCompleteType
          }
          cargo {
		  	endTransportingConditionId
			startTransportingConditionId
            containerParameters {
              identifier
              containerId
              eachContainerWeight
              containerQuantity
            }
			truckParameters {
				eachTruckWeight
				identifier
				truckId
				truckQuantity
			}
			customCargoParameters {
				identifier
				customCargoType
				baseParameters {
					quantity
					length
					width
					height
					weight
				}
				palletParameters {
					type
					quantity
					normativeHeightOfStacking
					weight
				}
				volumeParameters {
					volume
					weight
				}
			}
          }
        }
      }
    `;
  }
}
